// ─────────────────────────────────────────────────────────────────────────────
// The confetti cannon.
//
// Hand-rolled rather than a dependency, and that is a size decision more than a
// taste one: the app fires this from react-native-confetti-cannon, whose web
// equivalents all weigh more than the eighty lines below. It is one canvas, a
// bag of pieces with velocity and spin, gravity, and a fade at the end of life
// — which is the whole of what the phone's version does that anyone can see.
//
// ── IT PAINTS OVER THE LIVE SCREEN, NOT OVER A SCRIM ────────────────────────
// The per-word burst is fired while the corrected sentence that earned it is
// still on screen, and the user has to be able to read it. So the canvas is
// pointer-events:none, has no backdrop of its own, and removes itself the frame
// after the last piece dies. The finale's scrim is a separate element that
// happens to have confetti over it; the confetti never draws one.
// ─────────────────────────────────────────────────────────────────────────────

/** Cheerful, and deliberately not the app's UI palette — this is a reward, not
 *  a status. Same spread of hues the phone's cannon defaults to. */
const COLORS = ['#22d3ee', '#60a5fa', '#22c55e', '#f59e0b', '#f472b6', '#facc15'];

type Piece = {
  x: number; y: number;
  vx: number; vy: number;
  w: number; h: number;
  spin: number; angle: number;
  color: string;
  life: number;
};

/**
 * Fire `count` pieces from the top of `host`, and clean up after itself.
 *
 * Returns a canceller. Calling it mid-flight removes the canvas immediately,
 * which is what an unmounting screen needs — an orphaned rAF loop drawing into
 * a detached canvas is a leak that only shows up as a warm phone.
 */
export function fireConfetti(host: HTMLElement, count = 90): () => void {
  const canvas = document.createElement('canvas');
  canvas.className = 'confetti-canvas';
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const rect = host.getBoundingClientRect();
  const w = Math.max(1, rect.width);
  const h = Math.max(1, rect.height);
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  host.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); return () => {}; }
  ctx.scale(dpr, dpr);

  // Fired from just above the top edge, spread across the middle — the same
  // origin the app uses ({ x: width / 2, y: -20 }), widened so a wide browser
  // window does not get a thin jet down the centre.
  const pieces: Piece[] = Array.from({ length: count }, () => ({
    x: w / 2 + (Math.random() - 0.5) * w * 0.6,
    y: -20 - Math.random() * 40,
    vx: (Math.random() - 0.5) * 5.2,
    vy: 2 + Math.random() * 3.5,
    w: 6 + Math.random() * 6,
    h: 9 + Math.random() * 7,
    spin: (Math.random() - 0.5) * 0.32,
    angle: Math.random() * Math.PI,
    color: COLORS[(Math.random() * COLORS.length) | 0],
    life: 0,
  }));

  const GRAVITY = 0.12;
  const DRAG = 0.995;
  /** Frames a piece lives before it starts fading. ~2.5s at 60fps. */
  const FADE_AFTER = 150;
  const FADE_FRAMES = 45;

  let raf = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    ctx.clearRect(0, 0, w, h);
    let alive = 0;

    for (const p of pieces) {
      p.life += 1;
      p.vy += GRAVITY;
      p.vx *= DRAG;
      p.x += p.vx;
      p.y += p.vy;
      p.angle += p.spin;

      const fade = p.life <= FADE_AFTER
        ? 1
        : Math.max(0, 1 - (p.life - FADE_AFTER) / FADE_FRAMES);
      if (fade <= 0 || p.y > h + 40) continue;
      alive += 1;

      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }

    if (alive === 0) { canvas.remove(); return; }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return () => {
    stopped = true;
    cancelAnimationFrame(raf);
    canvas.remove();
  };
}

/** A full-window burst, for finishing an assignment. */
export function burst(): void {
  let host = document.getElementById('confetti-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'confetti-host';
    document.body.appendChild(host);
  }
  fireConfetti(host, 110);
}
